<template>
	<SharedMobileAppBanner
		:account="account"
		variant="bar"
		@visible="onVisible"
		@shown="onShown"
		@tapped="onTapped"
		@dismissed="onDismissed" />
</template>

<script setup lang="ts">
	import { datadogRum } from "@datadog/browser-rum";
	import { watch } from "vue";

	import SharedMobileAppBanner from "@common-ui/components/MobileAppBanner.vue";
	import {
		applyMobileAppBannerServerFields,
		type MobileAppBannerEvent,
		type MobileAppBannerTelemetry
	} from "@common-ui/utilities/mobileAppBanner";

	import api from "@/api";
	import type { MobileAppBannerAdminSurface } from "@/helpers/mobileAppBannerSurface";
	import type { Account } from "@/types/Account";

	/**
	 * LSP-21826: contact-admin host for the shared Open/Get app banner on the dashboard home page and
	 * the Start a Recording / Take Photos / Upload Photos pages.
	 * The shared component decides visibility and the label; this host records telemetry with admin's
	 * own Datadog SDK copy, reports the event to the API, and folds the returned snooze onto me.account.
	 * App.vue gates rendering on the mobile_app_banner flag, the routes in helpers/mobileAppBannerSurface.ts.
	 */
	const props = withDefaults(
		defineProps<{
			/** The signed-in user's current-org account from /me. The snooze ladder lives on it. */
			account: Account | null | undefined;
			/** Which admin page shows it, sent with every Datadog action so pages can be compared. */
			surface?: MobileAppBannerAdminSurface;
		}>(),
		{ surface: "admin-home" }
	);

	const emit = defineEmits<{ dismiss: []; visible: [boolean] }>();

	// App.vue keeps this instance when the route moves between banner pages (home to Start a Recording,
	// say) and only changes `surface`, while the shared banner emits `shown` once per mount. Count one
	// impression per surface instead, so each page's tap and dismiss rates have a matching denominator
	// (break-it on PR 6480).
	let bannerVisible = false;
	let lastShownPayload: MobileAppBannerTelemetry | null = null;
	const shownSurfaces = new Set<string>();

	function recordShown() {
		if (!lastShownPayload || shownSurfaces.has(props.surface)) return;
		shownSurfaces.add(props.surface);
		datadogRum.addAction("mobile_app_banner.shown", { ...lastShownPayload, surface: props.surface });
	}

	function onVisible(value: boolean) {
		bannerVisible = value;
		// AI review on PR 6480: a banner that hid and shows again (an org switch, a snooze ending) may now
		// be on a page that has no impression yet; the shared banner does not emit shown twice per mount.
		if (value) recordShown();
		emit("visible", value);
	}

	function onShown(payload: MobileAppBannerTelemetry) {
		lastShownPayload = payload;
		recordShown();
	}

	watch(
		() => props.surface,
		() => {
			if (bannerVisible) recordShown();
		}
	);

	function onTapped(payload: MobileAppBannerTelemetry) {
		// Telemetry only, by product decision: OPEN/GET never snoozes the banner, only the × does.
		datadogRum.addAction("mobile_app_banner.tapped", { ...payload, surface: props.surface });
	}

	function onDismissed(payload: MobileAppBannerTelemetry) {
		datadogRum.addAction("mobile_app_banner.dismissed", { ...payload, surface: props.surface });
		emit("dismiss");
		void record("dismissed");
	}

	/**
	 * Failures are logged, never surfaced: a banner that could not remember a dismissal is a nuisance,
	 * not something the user can act on, and it simply returns on the next page load.
	 */
	async function record(event: MobileAppBannerEvent) {
		const account = props.account;
		if (!account) return;
		try {
			const updated = await api.recordMobileAppBannerEvent(event, account.id);
			applyMobileAppBannerServerFields(account, updated);
		} catch (error) {
			console.warn(`MobileAppBanner: could not record "${event}" on the account; the banner will return next load.`, error);
		}
	}
</script>
